import moment from 'moment';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import { BACKEND_DATE_FORMAT } from '../../../config/helpers';

import DateRangePicker from '../Atoms/DateRangePicker';
import DropdownMultiple from '../Atoms/DropdownMultiple';
import Range from '../Atoms/Range';
import Icon from '../Atoms/Icon';
import { currencyCodeAndSymbolPropTypes } from '../../../util/currency';

const optionsPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  })
);

const propTypes = exact({
  currentCurrency: currencyCodeAndSymbolPropTypes,
  filterOptions: PropTypes.shape({
    accommodationTypeIds: PropTypes.arrayOf(PropTypes.string),
    durations: PropTypes.arrayOf(PropTypes.string),
    fromDate: PropTypes.string,
    toDate: PropTypes.string,
    inclusions: PropTypes.arrayOf(PropTypes.string),
    matches: PropTypes.arrayOf(PropTypes.string),
    packageTypes: PropTypes.string,
    page: PropTypes.number,
    priceFrom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    priceTo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    resultsPerPage: PropTypes.number,
    siteKey: PropTypes.string.isRequired,
    tournaments: PropTypes.arrayOf(PropTypes.string),
    destinations: PropTypes.arrayOf(PropTypes.string),
    minPrice: PropTypes.string.isRequired,
    maxPrice: PropTypes.string.isRequired,
  }),
  filters: PropTypes.shape({
    allOptionText: PropTypes.string,
    accommodationTypes: optionsPropTypes.isRequired,
    durations: optionsPropTypes.isRequired,
    inclusions: optionsPropTypes.isRequired,
    packageTypes: optionsPropTypes.isRequired,
    prices: PropTypes.arrayOf(
      PropTypes.shape({
        currency: PropTypes.string.isRequired,
        maximumPriceValue: PropTypes.number.isRequired,
        minimumPriceValue: PropTypes.number.isRequired,
      })
    ).isRequired,
    tournaments: PropTypes.arrayOf(
      PropTypes.shape({
        destinations: optionsPropTypes.isRequired,
        id: PropTypes.string.isRequired,
        matches: optionsPropTypes.isRequired,
        title: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  labels: PropTypes.shape({
    accommodationTypeLabelText: PropTypes.string.isRequired,
    dateLabelText: PropTypes.string.isRequired,
    destinationLabelText: PropTypes.string.isRequired,
    durationLabelText: PropTypes.string.isRequired,
    inclusionFilterLabelText: PropTypes.string.isRequired,
    matchLabelText: PropTypes.string.isRequired,
    priceLabelText: PropTypes.string.isRequired,
  }).isRequired,
  handleReset: PropTypes.func.isRequired,
  handleValueChange: PropTypes.func.isRequired,
  hideAccommodationTypeFilter: PropTypes.bool,
  hideDateRangeFilter: PropTypes.bool,
  hideDestinationFilter: PropTypes.bool,
  hideDurationFilter: PropTypes.bool,
  hideMatchFilter: PropTypes.bool,
  hidePriceRangeFilter: PropTypes.bool,
  resetButtonText: PropTypes.string,
  tournamentID: PropTypes.string,
  enableMatchFilterAndOrSelection: PropTypes.bool,
  includeAllMatchFilterItems: PropTypes.bool,
  matchFilterAndOrHelpText: PropTypes.string,
  matchFilterAndOrInfoText: PropTypes.string,
  handleMatchFilterAndOrChange: PropTypes.func,
});

const defaultProps = {
  filterOptions: {
    allOptionText: 'All',
  },
  hideAccommodationTypeFilter: false,
  hideDateRangeFilter: false,
  hideDestinationFilter: false,
  hideDurationFilter: false,
  hideMatchFilter: false,
  hidePriceRangeFilter: false,
  resetButtonText: 'Reset',
  tournamentID: null,
  enableMatchFilterAndOrSelection: false,
  includeAllMatchFilterItems: false,
  matchFilterAndOrHelpText: '',
  matchFilterAndOrInfoText: '',
  handleMatchFilterAndOrChange: null,
  currentCurrency: {
    code: 'GBP',
    symbol: '£',
  },
};

// The info trigger sits inside the footer's <label>, so swallow the click to
// stop it toggling the and/or checkbox. Focus still reveals the popover, which
// is what shows it on touch devices where there is no hover.
function handleInfoClick(event) {
  event.preventDefault();
  event.stopPropagation();
}

class Filter extends Component {
  constructor(props) {
    super(props);

    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleMultipleSelectChange = this.handleMultipleSelectChange.bind(this);
    this.handleRangeChange = this.handleRangeChange.bind(this);
    this.handleResetClick = this.handleResetClick.bind(this);
  }

  handleDateChange(dates) {
    const [fromDate, toDate] = dates;
    this.props.handleValueChange({
      fromDate: moment(fromDate).format(BACKEND_DATE_FORMAT),
      toDate: toDate ? moment(toDate).format(BACKEND_DATE_FORMAT) : '',
    });
  }

  handleMultipleSelectChange(fieldName, values) {
    const payload = { [fieldName]: values };
    this.props.handleValueChange(payload);
  }

  handleRangeChange(data) {
    const { priceFrom, priceTo, minPrice, maxPrice } = this.props.filterOptions;
    let newPriceFrom = data.values[0];
    let newPriceTo = data.values[1];

    // When the API response updates filterOptions.priceFrom/priceTo, Rheostat
    // may fire onChange because its values prop changed programmatically.
    // If the incoming values already match the current filter state, there is
    // nothing new to search for — skip to avoid a duplicate API call. This is
    // checked before the gap adjustment below, so a results-driven range the
    // customer never touched is not nudged and re-sent as if they had dragged.
    if (newPriceFrom === priceFrom && newPriceTo === priceTo) {
      return;
    }

    // When the two handles land on (or very near) the same position, Rheostat
    // can attribute the next click/drag to the wrong handle since they overlap
    // on screen. Keep a small minimum gap between them so they never fully
    // collide - adjust whichever handle just moved, not the one left in place.
    const minGap = Math.max(1, Math.round((Number(maxPrice) - Number(minPrice)) * 0.02));
    if (newPriceTo - newPriceFrom < minGap) {
      if (newPriceFrom !== priceFrom) {
        newPriceFrom = Math.max(Number(minPrice), newPriceTo - minGap);
      } else {
        newPriceTo = Math.min(Number(maxPrice), newPriceFrom + minGap);
      }
    }

    this.props.handleValueChange({ priceFrom: newPriceFrom, priceTo: newPriceTo });
  }

  handleResetClick() {
    this.props.handleReset();
  }

  render() {
    const mapOptions = (option) => ({
      text: option.title,
      value: option.id,
    });

    const { labels, currentCurrency, filterOptions, filters, tournamentID, resetButtonText } =
      this.props;

    const durationOptions = filters.durations.map(mapOptions);
    const accommodationTypeOptions = filters.accommodationTypes.map(mapOptions);

    let tournament = null;

    if (filters.tournaments.length === 1) {
      [tournament] = filters.tournaments;
    } else if (tournamentID) {
      [tournament] = filters.tournaments.filter((x) => x.id === tournamentID);
    }

    let matchOptions = [];
    let destinationOptions = [];

    if (tournament) {
      matchOptions = tournament.matches.map(mapOptions);
      destinationOptions = tournament.destinations.map(mapOptions);
    }

    return (
      <form className="PackageFilter PackageFilter_subSection">
        <div className="PackageFilter_fieldset">
          {!this.props.hideDateRangeFilter && (
            <div className="PackageFilter_field">
              <DateRangePicker
                blockName="PackageFilter_input"
                labelText={labels.dateLabelText}
                handleChange={this.handleDateChange}
                selectedValues={[
                  filterOptions.fromDate
                    ? moment(filterOptions.fromDate).toDate()
                    : moment().toDate(),
                  filterOptions.toDate ? moment(filterOptions.toDate).toDate() : null,
                ]}
              />
            </div>
          )}
          {!this.props.hideDurationFilter && (
            <div className="PackageFilter_field">
              <DropdownMultiple
                allOptionText={filters.allOptionText}
                blockName="PackageFilter_input"
                fieldName="durations"
                handleChange={this.handleMultipleSelectChange}
                labelText={labels.durationLabelText}
                options={durationOptions}
                selectedValues={filterOptions.durations}
              />
            </div>
          )}
          {!this.props.hideDestinationFilter && destinationOptions.length > 0 && (
            <div className="PackageFilter_field">
              <DropdownMultiple
                allOptionText={filters.allOptionText}
                blockName="PackageFilter_input"
                fieldName="destinations"
                handleChange={this.handleMultipleSelectChange}
                labelText={labels.destinationLabelText}
                options={destinationOptions}
                selectedValues={filterOptions.destinations}
              />
            </div>
          )}
          {!this.props.hideMatchFilter && matchOptions.length > 0 && (
            <div className="PackageFilter_field">
              <DropdownMultiple
                allOptionText={filters.allOptionText}
                blockName="PackageFilter_input"
                fieldName="matches"
                handleChange={this.handleMultipleSelectChange}
                labelText={labels.matchLabelText}
                options={matchOptions}
                selectedValues={filterOptions.matches}
                showCheckbox={this.props.enableMatchFilterAndOrSelection}
                footerMinSelectedCount={2}
                footer={
                  this.props.enableMatchFilterAndOrSelection ? (
                    <label
                      htmlFor="matchFilterAndOrCheckbox"
                      className="DropdownMultiple_checkboxRow"
                    >
                      <input
                        id="matchFilterAndOrCheckbox"
                        type="checkbox"
                        checked={this.props.includeAllMatchFilterItems}
                        onChange={this.props.handleMatchFilterAndOrChange}
                        className="DropdownMultiple_checkboxInput"
                      />
                      <span className="DropdownMultiple_footerText">
                        {this.props.matchFilterAndOrHelpText}
                      </span>
                      {this.props.matchFilterAndOrInfoText && (
                        <span className="DropdownMultiple_info">
                          <button
                            type="button"
                            className="DropdownMultiple_infoButton"
                            aria-label="More information"
                            aria-describedby="matchFilterAndOrInfo"
                            onClick={handleInfoClick}
                          >
                            <Icon id="tooltip" blockName="DropdownMultiple_infoIcon" />
                          </button>
                          <span
                            className="DropdownMultiple_infoPopover"
                            id="matchFilterAndOrInfo"
                            role="tooltip"
                          >
                            {this.props.matchFilterAndOrInfoText}
                          </span>
                        </span>
                      )}
                      <span className="DropdownMultiple_checkboxBox">
                        {this.props.includeAllMatchFilterItems && (
                          <Icon id="tick" blockName="DropdownMultiple_checkboxIcon" />
                        )}
                      </span>
                    </label>
                  ) : null
                }
              />
            </div>
          )}
          {!this.props.hideAccommodationTypeFilter && accommodationTypeOptions.length > 0 && (
            <div className="PackageFilter_field">
              <DropdownMultiple
                allOptionText={filters.allOptionText}
                blockName="PackageFilter_input"
                fieldName="accommodationTypeIds"
                handleChange={this.handleMultipleSelectChange}
                labelText={labels.accommodationTypeLabelText}
                options={accommodationTypeOptions}
                selectedValues={filterOptions.accommodationTypeIds}
              />
            </div>
          )}
          {!this.props.hidePriceRangeFilter && (
            <div className="PackageFilter_field">
              <Range
                blockName="PackageFilter_input"
                handleChange={this.handleRangeChange}
                labelText={labels.priceLabelText}
                labelValuePrefix={currentCurrency.symbol}
                maximumValue={filterOptions.maxPrice}
                minimumValue={filterOptions.minPrice}
                selectedValues={[filterOptions.priceFrom, filterOptions.priceTo]}
                isCurrency
              />
            </div>
          )}
        </div>
        <div className="PackageFilter_reset">
          <button
            type="button"
            className="PackageFilter_resetButton"
            onClick={this.handleResetClick}
          >
            {resetButtonText}
          </button>
        </div>
      </form>
    );
  }
}

Filter.propTypes = propTypes;
Filter.defaultProps = defaultProps;

export default Filter;
